// TranscriptSidebar - Unified sidebar for mobile and desktop with course sections
import React, { useState, useEffect } from 'react';
import { FileText, X, Pencil, Trash2, Folder, Plus, ChevronDown, ChevronRight, FolderOpen, Play } from 'lucide-react';
import { containerStyles } from './buttonStyles';
import { createFlexibleRepository } from '@/repositories/flexibleEntityRepository';

interface SavedTranscript {
  id: number | string;
  title: string;
  transcript: string;
  segments?: Array<{start: number, end: number, text: string}>;
  audioFileName?: string;
  createdAt: string;
  qaPairs?: Array<{question: string, answer: string}>;
  section?: string; // Course section ID
}

interface CourseSection {
  id: string;
  name: string; // e.g., "COA5628/Health & Wellness"
  color: string;
  isExpanded?: boolean;
}

interface TranscriptSidebarProps {
  savedTranscripts: SavedTranscript[];
  currentTranscriptId: number | string | null;
  editingTranscriptId: number | string | null;
  editedTitle: string;
  isMobile: boolean;
  showSidebar?: boolean;
  userId?: string; // Add userId to load sections from database
  onClose?: () => void;
  onLoadTranscript: (transcript: SavedTranscript) => void;
  onSetEditingTranscriptId: (id: number | string | null) => void;
  onSetEditedTitle: (title: string) => void;
  onRenameTranscript: (id: number | string, title: string) => void;
  onDeleteTranscript: (id: number | string) => void;
  onDeleteAllEmpty: () => void;
  onUpdateTranscriptSection?: (id: number | string, sectionId: string | undefined) => void; // New prop
}
export const TranscriptSidebar: React.FC<TranscriptSidebarProps> = ({
  savedTranscripts,
  currentTranscriptId,
  editingTranscriptId,
  editedTitle,
  isMobile,
  showSidebar,
  userId,
  onClose,
  onLoadTranscript,
  onSetEditingTranscriptId,
  onSetEditedTitle,
  onRenameTranscript,
  onDeleteTranscript,
  onDeleteAllEmpty,
  onUpdateTranscriptSection,
}) => {
  const [sections, setSections] = useState<CourseSection[]>([]);
  const settingsRepo = React.useMemo(() => createFlexibleRepository({ entityType: 'UserSettings', orderBy: 'updated_at DESC' }), []);
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionColor, setNewSectionColor] = useState('#8b5cf6');

  const sectionColors = [
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#10b981', label: 'Green' },
    { value: '#f59e0b', label: 'Orange' },
    { value: '#ef4444', label: 'Red' },
    { value: '#ec4899', label: 'Pink' }
  ];
  useEffect(() => {
    if (userId) {
      loadSections();
    }
  }, [userId]);

  const loadSections = async () => {
    try {
      // Load from database
      const items = await settingsRepo.list(userId);
      const userSettings = items.find((item: any) => item.userId === userId);
      
      if (userSettings?.transcriptSections) {
        setSections(userSettings.transcriptSections.map((s: CourseSection) => ({ ...s, isExpanded: s.isExpanded ?? true })));
      } else {
        // Fallback to localStorage for migration
        const saved = localStorage.getItem('whipt_transcript_sections');
        if (saved) {
          const localSections = JSON.parse(saved);
          setSections(localSections.map((s: CourseSection) => ({ ...s, isExpanded: s.isExpanded ?? true })));
          // Save to database for future
          await saveSections(localSections);
        }
      }
    } catch (err) {
      console.error('Failed to load sections:', err);
    }
  };

  const saveSections = async (newSections: CourseSection[]) => {
    setSections(newSections);
    
    if (!userId) return;
    
    try {
      // Save to database
      const items = await settingsRepo.list(userId);
      const existingSettings = items.find((item: any) => item.userId === userId);
      
      if (existingSettings) {
        // Update existing
        await settingsRepo.update(existingSettings.id, {
          userId,
          transcriptSections: newSections
        });
      } else {
        // Create new
        await settingsRepo.create({
          userId,
          transcriptSections: newSections
        });
      }
      
      // Also save to localStorage as backup
      localStorage.setItem('whipt_transcript_sections', JSON.stringify(newSections));
    } catch (err) {
      console.error('Failed to save sections:', err);
    }
  };

  const createSection = () => {
    if (!newSectionName.trim()) return;
    const newSection: CourseSection = {
      id: `section_${Date.now()}`,
      name: newSectionName.trim(),
      color: newSectionColor,
      isExpanded: true
    };
    saveSections([...sections, newSection]);
    setNewSectionName('');
    setShowNewSectionModal(false);
  };

  const toggleSection = (sectionId: string) => {
    saveSections(sections.map(s => s.id === sectionId ? { ...s, isExpanded: !s.isExpanded } : s));
  };

  const deleteSection = (sectionId: string) => {
    if (!confirm('Delete this section? Transcripts will be unsectioned.')) return;
    saveSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) setSelectedSection('all');
  };

  // Filter transcripts by section
  const filteredTranscripts = savedTranscripts.filter(t => {
    if (selectedSection === 'all') return true;
    if (selectedSection === 'unsectioned') return !t.section;
    return t.section === selectedSection;
  });

  const unsectionedCount = savedTranscripts.filter(t => !t.section).length;
  const hasEmptyTranscripts = savedTranscripts.some(t => !t.transcript || t.transcript.length === 0);

  const renderTranscriptCard = (saved: SavedTranscript) => {
    // Debug logging
    console.log('🎴 Rendering transcript card:', {
      id: saved.id,
      title: saved.title,
      titleType: typeof saved.title,
      audioFileName: saved.audioFileName,
      hasTranscript: !!saved.transcript
    });
    
    return (
    <div
      key={saved.id}
      className={containerStyles.transcriptCard(currentTranscriptId === saved.id)}
    >
      <button 
        onClick={() => {
          onLoadTranscript(saved);
          if (isMobile && onClose) onClose();
        }} 
        className="w-full text-left"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          {editingTranscriptId === saved.id ? (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => onSetEditedTitle(e.target.value)}
              onBlur={() => onRenameTranscript(saved.id, editedTitle)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onRenameTranscript(saved.id, editedTitle);
                if (e.key === 'Escape') {
                  onSetEditingTranscriptId(null);
                  onSetEditedTitle('');
                }
              }}
              className={containerStyles.titleInput}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-sm flex-shrink-0">
                {(saved as any).mode === 'document' ? '📄' : '🎤'}
              </span>
              <p className="font-semibold text-slate-200 text-xs line-clamp-2 flex-1">
                {saved.title || 'Untitled Transcript'}
              </p>
              {/* Quick rename button for untitled transcripts */}
              {!saved.title && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetEditingTranscriptId(saved.id);
                    onSetEditedTitle('');
                  }}
                  className="text-[8px] bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-1.5 py-0.5 rounded transition-colors"
                  title="Click to add a title"
                >
                  + Add Title
                </button>
              )}
            </div>
          )}
          {currentTranscriptId === saved.id && editingTranscriptId !== saved.id && (
            <span className={containerStyles.activeBadge}>Active</span>
          )}
        </div>
        
        <p className="text-[9px] text-slate-500 mb-1">ID: {saved.id}</p>
        <p className="text-slate-500 text-[10px] mt-1.5 line-clamp-2 leading-relaxed">
          {saved.transcript.substring(0, 80)}...
        </p>
        
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700/50 flex-wrap">
          <span className="text-slate-500 text-[9px]">
            {new Date(saved.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          
          <span className="text-slate-700">•</span>
          <span className="text-green-400 text-[9px] flex items-center gap-0.5">
            {(saved as any).mode === 'document' ? 'Document ✅' : 'Transcript ✅'}
          </span>
          
          {(saved as any).summary && (
            <>
              <span className="text-slate-700">•</span>
              <span className="text-purple-400 text-[9px] flex items-center gap-0.5">
                Summary ✅
              </span>
            </>
          )}
          
          {saved.qaPairs && saved.qaPairs.length > 0 && (
            <>
              <span className="text-slate-700">•</span>
              <span className="text-amber-400 text-[9px] font-semibold flex items-center gap-0.5">
                Q&A ✅ ({saved.qaPairs.length})
              </span>
            </>
          )}

          {/* Audio saved for playback indicator */}
          {((saved as any).audioDatabaseId || saved.audioFileName) && (
            <>
              <span className="text-slate-700">•</span>
              <span className="text-emerald-400 text-[9px] flex items-center gap-0.5">
                <Play className="w-2.5 h-2.5" /> Audio
              </span>
            </>
          )}
        </div>
      </button>
      
      {/* Section assignment dropdown */}
      {onUpdateTranscriptSection && sections.length > 0 && (
        <div className="mt-2">
          <label className="text-[9px] text-slate-500 mb-0.5 block">Course Section:</label>
          <select
            value={saved.section || 'none'}
            onChange={(e) => {
              e.stopPropagation();
              const newSection = e.target.value === 'none' ? undefined : e.target.value;
              onUpdateTranscriptSection(saved.id, newSection);
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[10px] text-slate-300 hover:border-purple-500 focus:border-purple-500 focus:outline-none transition-colors"
          >
            <option value="none">📂 Unsectioned (Remove from section)</option>
            {sections.map(s => (
              <option key={s.id} value={s.id}>📁 {s.name}</option>
            ))}
          </select>
        </div>
      )}
      
      {/* Action buttons - show on hover (desktop only) */}
      {!isMobile && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 pointer-events-none">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onSetEditingTranscriptId(saved.id);
              onSetEditedTitle(saved.title);
            }}
            className="p-1 bg-blue-600/80 hover:bg-blue-600 text-white rounded pointer-events-auto"
            title="Rename"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (confirm(`Delete "${saved.title}"?`)) {
                onDeleteTranscript(saved.id);
              }
            }}
            className="p-1 bg-red-600/80 hover:bg-red-600 text-white rounded pointer-events-auto"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
    );
  };

  const emptyState = (
    <div className="text-center p-4">
      <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
      <p className="text-xs text-slate-500">No saved transcripts yet</p>
      <p className="text-[10px] text-slate-600 mt-1">Upload audio or paste text to get started</p>
    </div>
  );

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-slate-700 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Saved Transcripts ({savedTranscripts.length})
          </h3>
          {isMobile && onClose && (
            <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded transition-colors">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        {/* Section Filter Buttons */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setSelectedSection('all')}
            className={`px-2 py-1 text-[10px] rounded ${
              selectedSection === 'all' ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All ({savedTranscripts.length})
          </button>
          <button
            onClick={() => setSelectedSection('unsectioned')}
            className={`px-2 py-1 text-[10px] rounded ${
              selectedSection === 'unsectioned' ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            Unsectioned ({unsectionedCount})
          </button>
          <button
            onClick={() => setShowNewSectionModal(true)}
            className="ml-auto p-1 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 rounded"
            title="Create section"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Course Sections */}
        {sections.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {sections.map(section => {
              const sectionTranscripts = savedTranscripts.filter(t => t.section === section.id);
              return (
                <div key={section.id} className="group/sec">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="p-0.5 hover:bg-slate-800 rounded"
                    >
                      {section.isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => setSelectedSection(section.id)}
                      className={`flex-1 flex items-center justify-between px-2 py-1 rounded text-[10px] ${
                        selectedSection === section.id ? 'bg-purple-600/30 text-purple-200' : 'hover:bg-slate-800 text-slate-400'
                      }`}
                    >
                      <span className="flex items-center gap-1 truncate">
                        <Folder className="w-3 h-3" style={{ color: section.color }} />
                        {section.name}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-slate-900 rounded">{sectionTranscripts.length}</span>
                    </button>
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="opacity-0 group-hover/sec:opacity-100 p-0.5 hover:bg-red-600/20 rounded"
                    >
                      <X className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                  {section.isExpanded && sectionTranscripts.length > 0 && (
                    <div className="ml-5 mt-1 space-y-1">
                      {sectionTranscripts.map(t => (
                        <button
                          key={t.id}
                          onClick={() => {
                            onLoadTranscript(t);
                            if (isMobile && onClose) onClose();
                          }}
                          className={`w-full text-left px-2 py-1 rounded text-[10px] truncate ${
                            currentTranscriptId === t.id ? 'bg-purple-500/20 text-purple-200' : 'text-slate-500 hover:bg-slate-800'
                          }`}
                        >
                          {t.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasEmptyTranscripts && (
          <button
            onClick={onDeleteAllEmpty}
            className="w-full px-2 py-1 text-[10px] bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded border border-red-600/40"
          >
            Clean Up Empty
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {filteredTranscripts.length === 0 ? emptyState : filteredTranscripts.map(renderTranscriptCard)}
      </div>

      {/* New Section Modal */}
      {showNewSectionModal && (
        <div className="absolute inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 w-full max-w-sm">
            <h4 className="text-sm font-bold text-white mb-3">Create Course Section</h4>
            <input
              type="text"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="e.g. COA5628/Health & Wellness"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white text-xs mb-3"
              autoFocus
            />
            <div className="grid grid-cols-3 gap-2 mb-3">
              {sectionColors.map(c => (
                <button
                  key={c.value}
                  onClick={() => setNewSectionColor(c.value)}
                  className={`px-2 py-1 rounded text-[10px] ${
                    newSectionColor === c.value ? 'ring-2 ring-white' : ''
                  }`}
                  style={{ backgroundColor: `${c.value}30`, color: c.value }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={createSection}
                className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowNewSectionModal(false);
                  setNewSectionName('');
                }}
                className="flex-1 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Mobile overlay
  if (isMobile) {
    return showSidebar ? (
      <div 
        className="fixed inset-0 bg-black/80 z-50 md:hidden"
        onClick={onClose}
      >
        <div 
          className={containerStyles.mobileSidebar}
          onClick={(e) => e.stopPropagation()}
        >
          {sidebarContent}
        </div>
      </div>
    ) : null;
  }

  // Desktop sidebar
  return (
    <div className={containerStyles.desktopSidebar}>
      {sidebarContent}
    </div>
  );
};
